

import './NewProject.scss';

import React, { ChangeEvent, FC, useState } from 'react';
import { Project } from 'models/Project';
import { Col, Form, Row } from 'react-bootstrap';

import { WizardStepInfo } from '../SetupWizard/WizardStepInfo';
import WizardActions from '../WizardActions/WizardActions';

interface NewProjectProps extends WizardStepInfo {
  cachedProject: Project;
  onUpdate: (entity: string, key: string, value: any) => void;
}

const NewProject: FC<NewProjectProps> = ({ cachedProject, onUpdate, ...wizardContext }) => {
  const onEntityFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    onUpdate('Project', event.target.name, event.target.value);
  };
  const [validated, setValidated] = useState(false);
  const onFormValidated = (validationResult: boolean) => {
    setValidated(validationResult);
  };
  const modifiedWizardContext = { ...wizardContext, onFormValidated };

  return (
    <div className="NewProject">
      <Form validated={validated}>
        <h1 className='text-center'>
          Project Setup
        </h1>
        <fieldset>
          <legend>Project Info</legend>
          <Form.Group controlId="name">
            <Form.Label>Project Name</Form.Label>
            <Form.Control
              type="text"
              value={cachedProject.name || ''}
              onChange={onEntityFieldChange}
              aria-label="Project Name"
              name="name"
              placeholder="Project Name"
              className="mb-2 font-italic"
              autoComplete="organization"
              tabIndex={1}
              required
            />
            <Form.Control.Feedback type="invalid">
              Please enter a project name
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="websiteUrl">
            <Form.Label>Website URL</Form.Label>
            <Form.Control
              type="url"
              value={cachedProject.websiteUrl}
              onChange={onEntityFieldChange}
              aria-label="Website URL"
              name="websiteUrl"
              placeholder="https://mywebsite.com"
              className="mb-2 font-italic"
              autoComplete="url"
              tabIndex={2}
              required
            />
            <Form.Control.Feedback type="invalid">
              Please enter a valid website URL.
            </Form.Control.Feedback>
          </Form.Group>
        </fieldset>
        <fieldset>
          <legend>Project Manager</legend>
          <p>
            Your Project Manager will receive general notifications about the status of content.
          </p>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="pmName" xs="12" sm="6">
              <Form.Label>Project Manager&#39;s Name</Form.Label>
              <Form.Control
                type="text"
                value={cachedProject.pmName}
                onChange={onEntityFieldChange}
                aria-label="First Last"
                name="pmName"
                placeholder="First Last"
                className="mb-2 font-italic"
                tabIndex={3}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="pmEmailAddress" xs="12" sm="6">
              <Form.Label>Project Manager&#39;s Email</Form.Label>
              <Form.Control
                type="email"
                value={cachedProject.pmEmailAddress}
                onChange={onEntityFieldChange}
                aria-label="Enter project manager's email"
                name="pmEmailAddress"
                placeholder="primary_email@domain.com"
                className="mb-2 font-italic"
                tabIndex={4}
              />
            </Form.Group>
          </Row>
        </fieldset>
        <fieldset>
          <legend>Writer</legend>
          <p>
            Your writer will receive notifications about when content is due, or has been approved
            or rejected.
          </p>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="writerName" xs="12" sm="6">
              <Form.Label>Writer&#39;s Name</Form.Label>
              <Form.Control
                type="text"
                value={cachedProject.writerName}
                onChange={onEntityFieldChange}
                aria-label="First Last"
                name="writerName"
                placeholder="First Last"
                className="mb-2 font-italic"
                tabIndex={5}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="writerEmailAddress" xs="12" sm="6">
              <Form.Label>Writer&#39;s Email</Form.Label>
              <Form.Control
                type="email"
                value={cachedProject.writerEmailAddress}
                onChange={onEntityFieldChange}
                aria-label="Enter writer's email"
                name="writerEmailAddress"
                placeholder="primary_email@domain.com"
                className="mb-2 font-italic"
                tabIndex={6}
              />
            </Form.Group>
          </Row>
        </fieldset>
        <fieldset>
          <legend>Content Approvers</legend>
          <p>Content approvers receive notifications when new content is ready for review.</p>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="approver1Name" xs="12" sm="6">
              <Form.Label>Primary Approver&#39;s Name</Form.Label>
              <Form.Control
                type="text"
                value={cachedProject.approver1Name}
                onChange={onEntityFieldChange}
                aria-label="First Last"
                name="approver1Name"
                placeholder="First Last"
                className="mb-2 font-italic"
                tabIndex={7}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="approver1Email" xs="12" sm="6">
              <Form.Label>Primary Approver&#39;s Email</Form.Label>
              <Form.Control
                type="email"
                value={cachedProject.approver1Email}
                onChange={onEntityFieldChange}
                aria-label="Enter primary approver's email"
                name="approver1Email"
                placeholder="primary_email@domain.com"
                className="mb-2 font-italic"
                tabIndex={8}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="approver2Name" xs="12" sm="6">
              <Form.Label>Secondary Approver&#39;s Name</Form.Label>
              <Form.Control
                type="text"
                value={cachedProject.approver2Name}
                onChange={onEntityFieldChange}
                aria-label="First Last"
                name="approver2Name"
                placeholder="First Last"
                className="mb-2 font-italic"
                tabIndex={9}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="approver2Email" xs="12" sm="6">
              <Form.Label>Secondary Approver&#39;s Email</Form.Label>
              <Form.Control
                type="email"
                value={cachedProject.approver2Email}
                onChange={onEntityFieldChange}
                aria-label="Enter primary approver's email"
                name="approver2Email"
                placeholder="secondary_email@domain.com"
                className="mb-2 font-italic"
                tabIndex={10}
              />
            </Form.Group>
          </Row>
        </fieldset>
        <fieldset>
          <legend>Custom Article Footer</legend>
          <Row>
            <Form.Group as={Col} controlId="articleFooterTemplate" xs="12">
              <Form.Label>(Optional) Custom HTML to add to the footer of every article</Form.Label>
              <Form.Text className="text-muted" as={Col} xs="12">
                If you&#39;re not sure what to add here, leave it blank. You can always update it
                later!
              </Form.Text>
              <Form.Control
                as="textarea"
                rows={8}
                value={cachedProject.articleFooterTemplate}
                onChange={onEntityFieldChange}
                aria-label="enter custom footer HTML here"
                name="articleFooterTemplate"
                placeholder="<div>enter custom footer HTML here...</div>"
                className="mb-2 font-italic"
                tabIndex={11}
              />
            </Form.Group>
          </Row>
        </fieldset>
        <fieldset>
          <legend>GPT Customizations</legend>
          <Row>
            <Form.Group as={Col} controlId="gptApiKey" xs="12">
              <Form.Label>ChatGPT Api Key</Form.Label>
              <Form.Text className="text-muted" as={Col} xs="12">
                If you would like to use GPT-4 models, you must enter your own API key.
              </Form.Text>
              <Form.Control
                value={cachedProject.gptApiKey}
                onChange={onEntityFieldChange}
                aria-label="enter chatGPT api key here"
                name="gptApiKey"
                id="gptApiKey"
                className="mb-2 font-italic"
                tabIndex={12}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} xs="12">
              <Form.Label>Project Offer</Form.Label>
              <Form.Text className="text-muted" as={Col} xs="12">
                <strong>{cachedProject.name}</strong> helps <strong>{cachedProject.targetAudience}</strong> by <strong>{cachedProject.solutionForAudience}</strong>.
              </Form.Text>
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} controlId="targetAudience" xs="4">
              <Form.Label>Target Audience</Form.Label>
              <Form.Text className="text-muted" as={Col} xs="12">
                Who is your ideal customer?
              </Form.Text>
              <Form.Control
                value={cachedProject.targetAudience}
                onChange={onEntityFieldChange}
                aria-label="enter your ideal customer"
                name="targetAudience"
                id="targetAudience"
                className="mb-2 font-italic"
                tabIndex={13}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="solutionForAudience" xs="8">
              <Form.Label>Solution</Form.Label>
              <Form.Text className="text-muted" as={Col} xs="12">
                What is the primary solution you provide your customers with?
              </Form.Text>
              <Form.Control
                value={cachedProject.solutionForAudience}
                onChange={onEntityFieldChange}
                aria-label="enter the primary solution you help your customers with"
                name="solutionForAudience"
                id="solutionForAudience"
                className="mb-2 font-italic"
                tabIndex={14}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} controlId="globalNegativeTerms" xs="12">
              <Form.Label>Global Negative Terms</Form.Label>
              <Form.Text className="text-muted" as={Col} xs="12">
                Enter any terms which AI-generated content should always avoid using.
              </Form.Text>
              <Form.Control
                value={cachedProject.globalNegativeTerms}
                onChange={onEntityFieldChange}
                aria-label="enter terms GPT should avoid using"
                name="globalNegativeTerms"
                id="globalNegativeTerms"
                className="mb-2 font-italic"
                tabIndex={15}
              />
            </Form.Group>
          </Row>
        </fieldset>
        <WizardActions {...modifiedWizardContext} />
      </Form>
    </div>
  );
};

export default NewProject;
